import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * PromoBanner — first-party "sponsorship" deck for the SPA homepage.
 * Mirrors the Astro flip-deck (Base.astro) so the homepage matches the
 * garage/search/kostos pages. First-party + neutral class names (v-*) so
 * ad-blockers don't strip it. GA4 partner_view / partner_click tracking.
 *
 * Mounted globally in App.js; renders only on the homepage ("/" or "/en").
 */

// Fallback used until /api/banners loads (or if it fails). The live endpoint
// returns the FULL deck: every active offer + Pro garages (Κολάγγης, DMS) + house.
// NOTE: 'dms-stage1' removed 21/8/26 — it was the first fallback creative shown on
// every homepage load before /api/banners resolved, so it racked up ~4.090 impressions
// at 0.02% CTR (dead inventory, no clicks). The live deck's labelled DMS creatives stay.
const FALLBACK_CREATIVES = [
  {
    id: "kolaggis",
    kind: "text",
    eyebrow: "Συνεργείο Κολάγγης · Αθήνα",
    headline: "Αλλαγή λαδιών €40 — με δώρο ελέγχους",
    sub: "Δωρεάν τεχνικός & διαγνωστικός έλεγχος, κλιματισμός, ελαστικά.",
    cta: "Δες την προσφορά",
    href: "/offers",
    external: false,
    accent: "#ff6a1f",
  },
  {
    id: "pamebrosta",
    kind: "text",
    eyebrow: "Για επαγγελματίες",
    headline: "Θες δικό σου site;",
    sub: "Κατασκευή επαγγελματικού site από 299€ — γρήγορα, με σωστό SEO.",
    cta: "Μιλάμε",
    href: "https://pamebrosta.gr/",
    external: true,
    accent: "#12b3a6",
  },
  {
    id: "vroum-pro",
    kind: "text",
    eyebrow: "Για συνεργεία",
    headline: "Είσαι συνεργείο; Διαχειρίσου το προφίλ σου στο VROUM.",
    sub: "VROUM Pro — verified badge, φωτογραφίες, στατιστικά & lead inbox. €19,90/μήνα. Η οργανική κατάταξη μένει δωρεάν & αλγοριθμική.",
    cta: "Δες το Pro",
    href: "/pro",
    external: false,
    accent: "#ff6a1f",
  },
  {
    id: "advertise",
    kind: "text",
    eyebrow: "Διαφημίσου στο VROUM",
    headline: "Η διαφήμισή σου εδώ.",
    sub: "Χιλιάδες οδηγοί κάθε μέρα. Βάλε την επιχείρησή σου μπροστά τους.",
    cta: "Ζήτα προσφορά",
    href: "/diafimisou",
    external: false,
    accent: "#7c3aed",
  },
];

const FLIP_MS = 9000;

// GA4 for real-time + a first-party beacon to /track/event → analytics_events so
// the admin 'Banners' tab shows 'πόσα clicks βαρέσανε' without GA4. Clicks always
// persist; views persist only the initial impression (persist=true) to stay lean.
function track(event, id, persist, advertiser) {
  const adv = advertiser || id;
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, { partner_id: id, advertiser: adv, surface: "home" });
    }
  } catch (_) {}
  if (persist) {
    try {
      fetch("/api/track/event", {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: event, params: { partner_id: id, advertiser: adv, surface: "home" } }),
      });
    } catch (_) {}
  }
}

export default function PromoBanner({ skipHome = false, slim = false, rail = false, startAt = 0 }) {
  const { pathname } = useLocation();
  const [idx, setIdx] = useState(startAt);
  const [creatives, setCreatives] = useState(FALLBACK_CREATIVES);
  const seen = useRef(new Set());
  const timer = useRef(null);
  const hovering = useRef(false);

  // Pull the full live deck (offers + Pro garages + house ads) once.
  useEffect(() => {
    let alive = true;
    fetch("/api/banners")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d && Array.isArray(d.banners) && d.banners.length) {
          setCreatives(d.banners);
          setIdx(startAt % d.banners.length);
          seen.current = new Set();
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Show on content pages across the SPA (more inventory, per owner's "banners
  // everywhere"), but NEVER on transactional/app screens where an ad is wrong.
  const BLOCK = ["/checkout", "/dashboard", "/login", "/auth", "/admin", "/register",
    "/synergeio", "/diafimisou", "/my-garage", "/adminhere", "/data-quality"];
  const isHomepage = pathname === "/" || pathname === "/en" || pathname === "/en/";
  // skipHome = the global App.js mount; the homepage renders its own banner high
  // up inside HomeV4, so the global one steps aside there to avoid a double.
  const onHome = !BLOCK.some((p) => pathname.startsWith(p) || pathname.startsWith("/en" + p))
    && !(skipHome && isHomepage);

  // rotate
  useEffect(() => {
    if (!onHome) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timer.current = setInterval(() => {
      if (hovering.current) return;
      setIdx((i) => (i + 1) % creatives.length);
    }, FLIP_MS);
    return () => clearInterval(timer.current);
  }, [onHome, creatives.length]);

  // view tracking (once per creative)
  useEffect(() => {
    if (!onHome) return;
    const c = creatives[idx];
    // Count ONE genuine impression per placement — the initial creative that is
    // actually on screen. Rotation still cycles for variety, but auto-flipped
    // creatives (a brief flash the user usually scrolls past) are no longer
    // counted as paid views. Kills the ~11-views/session inflation and gives an
    // honest, defensible number for billing. startAt varies per placement, so
    // over many sessions each advertiser lands the counted slot fairly.
    if (c && seen.current.size === 0) {
      seen.current.add(c.id);
      track("partner_view", c.id, true, c.advertiser);
    }
  }, [idx, onHome, creatives]);

  if (!onHome) return null;

  // Slim mode — a discreet one-line sponsored strip (used above the hero). Still
  // rotates (idx cycles via the effect above) and tracks view/click.
  if (slim) {
    const c = creatives[idx] || creatives[0];
    const ext = c.external
      ? { target: "_blank", rel: "sponsored noopener" }
      : { rel: "sponsored" };
    return (
      <a
        className="v-strip"
        href={c.href}
        style={{ "--vac": c.accent }}
        onClick={() => track("partner_click", c.id, true, c.advertiser)}
        onMouseEnter={() => (hovering.current = true)}
        onMouseLeave={() => (hovering.current = false)}
        {...ext}
      >
        <style>{STRIP_CSS}</style>
        <span className="v-strip-tag">{c.tag || "Χορηγία"}</span>
        <span className="v-strip-h">{c.headline}</span>
        <span className="v-strip-cta" aria-hidden="true">{c.cta} →</span>
      </a>
    );
  }

  // Rail mode — a tall vertical "skyscraper" card for desktop side gutters.
  if (rail) {
    const c = creatives[idx] || creatives[0];
    const ext = c.external
      ? { target: "_blank", rel: "sponsored noopener" }
      : { rel: "sponsored" };
    return (
      <a
        className="v-rail"
        href={c.href}
        style={{ "--vac": c.accent }}
        onClick={() => track("partner_click", c.id, true, c.advertiser)}
        onMouseEnter={() => (hovering.current = true)}
        onMouseLeave={() => (hovering.current = false)}
        {...ext}
      >
        <style>{RAIL_CSS}</style>
        {c.kind === "photo" && c.image && (
          <span className="v-rail-photo" style={{ backgroundImage: `url(${c.image})` }}>
            <span className="v-rail-tag">{c.tag || "Χορηγία"}</span>
          </span>
        )}
        <span className="v-rail-in">
          {!(c.kind === "photo" && c.image) && <span className="v-rail-tag flat">{c.tag || "Χορηγία"}</span>}
          {c.eyebrow && <span className="v-rail-ey">{c.eyebrow}</span>}
          <b className="v-rail-h">{c.headline}</b>
          <span className="v-rail-cta">{c.cta} →</span>
        </span>
      </a>
    );
  }

  return (
    <aside className="v-slot" aria-label="Χορηγία">
      <style>{CSS}</style>
      <div className="v-stage">
        <div className="v-deck">
          {creatives.map((c, i) => (
            <a
              key={c.id}
              className={`v-card v-k-${c.kind}${i === idx ? " on" : ""}`}
              href={c.href}
              style={{ "--vac": c.accent }}
              aria-hidden={i !== idx}
              tabIndex={i !== idx ? -1 : 0}
              onClick={() => track("partner_click", c.id, true, c.advertiser)}
              {...(c.external
                ? { target: "_blank", rel: "sponsored noopener" }
                : { rel: "sponsored" })}
            >
              {c.kind === "photo" && c.image && (
                <>
                  <span
                    className="v-photo"
                    style={{ backgroundImage: `url(${c.image})` }}
                  />
                  <span className="v-veil" />
                </>
              )}
              <span className="v-tag">
                {c.tag || "Χορηγία"} · <b>VROU<i>M</i></b>
              </span>
              <span className="v-in">
                {c.eyebrow && <span className="v-k">{c.eyebrow}</span>}
                <b className="v-h">{c.headline}</b>
                {c.sub && <span className="v-s">{c.sub}</span>}
                <span className="v-c">{c.cta} →</span>
              </span>
            </a>
          ))}
        </div>
      </div>
      <div className="v-dots" role="tablist">
        {creatives.map((c, i) => (
          <i key={c.id} className={i === idx ? "on" : ""} />
        ))}
      </div>
    </aside>
  );
}

const CSS = `
/* Blend into the page instead of floating like a window: full-bleed, no frame/
   shadow, edges feathered into the background via a mask, and a dark scrim under
   the content so text stays readable on ANY page (light or dark). */
.v-slot{max-width:none;margin:0;padding:0;display:block}
.v-stage{perspective:1400px}
/* Full-bleed band (no centred box, no frame) reads as part of the page, not a
   floating window. A soft bottom border-fade blends it into the section below. */
.v-deck{position:relative;transform-style:preserve-3d;min-height:128px;width:100%;margin:0;
  box-shadow:inset 0 -1px 0 rgba(255,255,255,.04)}
.v-card{position:absolute;inset:0;display:flex;flex-direction:column;justify-content:center;
  gap:2px;padding:24px 32px;border-radius:0;overflow:hidden;text-decoration:none;color:#eef1f6;
  background:linear-gradient(100deg,rgba(11,12,16,.82),rgba(11,12,16,.34));border:0;box-shadow:none;
  opacity:0;transform:rotateX(90deg);transform-origin:top center;
  transition:transform .62s cubic-bezier(.2,.7,.2,1),opacity .4s;pointer-events:none}
.v-card.on{opacity:1;transform:rotateX(0);pointer-events:auto;z-index:2}
.v-k-photo{background:transparent;color:#fff}
.v-photo{position:absolute;inset:0;background-size:cover;background-position:center;opacity:.5}
.v-veil{position:absolute;inset:0;background:linear-gradient(90deg,rgba(10,11,14,.94) 0%,rgba(10,11,14,.7) 45%,rgba(10,11,14,.35) 100%)}
.v-card>*{position:relative;z-index:1}
.v-tag{position:absolute;top:14px;right:16px;font-size:10.5px;font-weight:700;letter-spacing:.02em;
  color:#8a9099;z-index:2}
.v-k-photo .v-tag{color:rgba(255,255,255,.66)}
.v-tag b{color:inherit;font-weight:800}
.v-tag i{font-style:normal;color:#ff6a1f}
.v-in{display:flex;flex-direction:column;gap:3px;max-width:82%}
.v-k{font-size:11.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--vac,#ff6a1f);padding-right:96px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.v-k-photo .v-k{color:#ff7a3c}
.v-h{font-size:20px;font-weight:800;letter-spacing:-.02em;line-height:1.18}
.v-s{font-size:13.5px;color:rgba(255,255,255,.76);line-height:1.4;margin-top:1px}
.v-k-photo .v-s{color:rgba(255,255,255,.78)}
.v-c{margin-top:9px;font-size:13.5px;font-weight:800;color:var(--vac,#ff6a1f)}
.v-k-photo .v-c{color:#ff8a52}
.v-dots{display:flex;gap:7px;justify-content:center;margin-top:16px}
.v-dots i{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.22);transition:.3s;display:block}
.v-dots i.on{background:#ff5a1f;width:20px;border-radius:4px}
@media(max-width:560px){
  .v-slot{padding:8px 0 14px}
  .v-card{padding:20px 20px}
  .v-in{max-width:100%}
  .v-h{font-size:18px}
  .v-deck{min-height:168px}
}
@media(prefers-reduced-motion:reduce){
  .v-card{transition:opacity .3s}
  .v-card{transform:none}
}
`;

const STRIP_CSS = `
.v-strip{display:flex;align-items:center;gap:10px;max-width:1080px;margin:10px auto 2px;padding:8px 14px;
  border-radius:10px;background:#f6f7f9;border:1px solid #eceef1;text-decoration:none;overflow:hidden;
  font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Roboto,sans-serif;color:#1d1d1f;
  transition:background .15s,border-color .15s}
.v-strip:hover{background:#f0f2f5;border-color:#e0e3e8}
.v-strip-tag{flex:none;font-size:9px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;
  color:#9aa0a6;border:1px solid #e3e6ea;border-radius:99px;padding:2px 7px;background:#fff}
.v-strip-h{flex:1;min-width:0;font-size:13.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.v-strip-cta{flex:none;font-size:13px;font-weight:800;color:var(--vac,#ff6a1f)}
@media(max-width:560px){.v-strip{margin:8px 12px 2px;gap:8px}.v-strip-cta{display:none}.v-strip-h{font-size:13px}}
`;

const RAIL_CSS = `
.v-rail{display:flex;flex-direction:column;width:100%;border-radius:14px;overflow:hidden;text-decoration:none;
  background:#fff;border:1px solid rgba(0,0,0,.07);box-shadow:0 4px 16px -10px rgba(0,0,0,.22);color:#1d1d1f;
  font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Roboto,sans-serif;
  transition:transform .16s,box-shadow .16s,border-color .16s}
.v-rail:hover{transform:translateY(-2px);box-shadow:0 12px 26px -14px rgba(0,0,0,.3);border-color:rgba(0,0,0,.12)}
.v-rail-photo{position:relative;height:88px;background-size:cover;background-position:center;background-color:#0e0f13}
.v-rail-photo::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.05),rgba(0,0,0,.28))}
.v-rail-tag{position:absolute;top:9px;left:10px;z-index:1;font-size:8.5px;font-weight:800;letter-spacing:.05em;
  text-transform:uppercase;color:#fff;background:rgba(0,0,0,.42);backdrop-filter:blur(4px);
  padding:3px 7px;border-radius:99px}
.v-rail-tag.flat{position:static;display:inline-block;color:#9aa0a6;background:#f2f3f5;margin-bottom:2px}
.v-rail-in{display:flex;flex-direction:column;gap:2px;padding:9px 11px 11px;min-width:0}
.v-rail-ey{font-size:9.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--vac,#ff6a1f);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.v-rail-h{font-size:14px;font-weight:800;letter-spacing:-.015em;line-height:1.22;color:#1d1d1f;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.v-rail-s{font-size:11.5px;color:#6b7078;line-height:1.38;margin-top:1px;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.v-rail-cta{margin-top:6px;font-size:12px;font-weight:800;color:var(--vac,#ff6a1f)}
`;
